import type {
  ClientContextInput,
  EventIdentity,
  PushSubscriptionPatchPayload,
  PushSubscriptionRemovePayload,
  PushSubscriptionUpsertPayload,
  RegisterUserPayload,
  UpdateUserPayload,
} from '../types';
import { MotiSigHttpClient } from './httpClient';

export interface RegisterUserResponse {
  success: true;
  userId: string;
  anonymousId?: string;
}

export interface EventResponse {
  success: boolean;
  message: string;
}

/** Typed MotiSig AI REST client (web). */
export class MotiSigApi {
  constructor(private readonly http: MotiSigHttpClient) {}

  async identify(
    userId: string,
    payload: Omit<RegisterUserPayload, 'id' | 'platform'>,
  ): Promise<RegisterUserResponse> {
    const res = await this.http.request<RegisterUserResponse>('POST', '/users', {
      id: userId,
      ...payload,
    });
    return this.http.assertOk(res);
  }

  async updateUser(userId: string, payload: UpdateUserPayload): Promise<void> {
    const res = await this.http.request<unknown>(
      'PATCH',
      `/users/${encodeURIComponent(userId)}`,
      payload
    );
    this.http.assertOk(res);
  }

  async addTags(identity: EventIdentity, tags: string[]): Promise<void> {
    const pathId = identity.userId ?? identity.anonymousId;
    const res = await this.http.request<unknown>(
      'POST',
      `/users/${encodeURIComponent(pathId!)}/tags`,
      {
        tags,
        ...this.anonymousBodyFields(identity),
      },
    );
    this.http.assertOk(res);
  }

  async removeTags(identity: EventIdentity, tags: string[]): Promise<void> {
    const pathId = identity.userId ?? identity.anonymousId;
    const res = await this.http.request<unknown>(
      'DELETE',
      `/users/${encodeURIComponent(pathId!)}/tags`,
      {
        tags,
        ...this.anonymousBodyFields(identity),
      },
    );
    this.http.assertOk(res);
  }

  async addOrUpdateAttributes(
    identity: EventIdentity,
    attributes: Record<string, unknown>,
  ): Promise<void> {
    const pathId = identity.userId ?? identity.anonymousId;
    const res = await this.http.request<unknown>(
      'POST',
      `/users/${encodeURIComponent(pathId!)}/attributes`,
      {
        attributes,
        ...this.anonymousBodyFields(identity),
      },
    );
    this.http.assertOk(res);
  }

  async removeAttributes(identity: EventIdentity, keys: string[]): Promise<void> {
    const pathId = identity.userId ?? identity.anonymousId;
    const res = await this.http.request<unknown>(
      'DELETE',
      `/users/${encodeURIComponent(pathId!)}/attributes`,
      {
        keys,
        ...this.anonymousBodyFields(identity),
      },
    );
    this.http.assertOk(res);
  }

  private anonymousBodyFields(identity: EventIdentity): {
    anonymousId?: string;
    anonymousIdProvider?: EventIdentity['anonymousIdProvider'];
  } {
    if (identity.userId) return {};
    return {
      anonymousId: identity.anonymousId,
      anonymousIdProvider: identity.anonymousIdProvider,
    };
  }

  async event(
    identity: EventIdentity,
    eventName: string,
    eventData?: Record<string, unknown>,
    context?: ClientContextInput
  ): Promise<string> {
    const body: {
      userId?: string;
      anonymousId?: string;
      anonymousIdProvider?: EventIdentity['anonymousIdProvider'];
      eventName: string;
      eventData?: Record<string, unknown>;
      context?: ClientContextInput;
    } = {
      userId: identity.userId,
      anonymousId: identity.anonymousId,
      anonymousIdProvider: identity.anonymousIdProvider,
      eventName,
    };
    if (eventData !== undefined) body.eventData = eventData;
    if (context !== undefined) body.context = context;
    const res = await this.http.request<EventResponse>('POST', '/events', body);
    const data = this.http.assertOk(res);
    return data.message;
  }

  async activityBatch(events: import('../types').ActivityBatchEvent[]): Promise<number> {
    const res = await this.http.request<{ success: boolean; recorded: number }>(
      'POST',
      '/activity/batch',
      { events }
    );
    const data = this.http.assertOk(res);
    return data.recorded;
  }

  async getWebConfig(): Promise<{ vapidPublicKey: string | null }> {
    const res = await this.http.request<{ vapidPublicKey: string | null }>('GET', '/web-config');
    return this.http.assertOk(res);
  }

  async upsertPushSubscription(
    identity: EventIdentity,
    payload: PushSubscriptionUpsertPayload,
  ): Promise<void> {
    const pathId = identity.userId ?? identity.anonymousId;
    const res = await this.http.request<unknown>(
      'POST',
      `/users/${encodeURIComponent(pathId!)}/push-subscriptions`,
      {
        ...payload,
        ...this.anonymousBodyFields(identity),
      },
    );
    this.http.assertOk(res);
  }

  async patchPushSubscription(
    identity: EventIdentity,
    payload: PushSubscriptionPatchPayload,
  ): Promise<void> {
    const pathId = identity.userId ?? identity.anonymousId;
    const res = await this.http.request<unknown>(
      'PATCH',
      `/users/${encodeURIComponent(pathId!)}/push-subscriptions`,
      {
        ...payload,
        ...this.anonymousBodyFields(identity),
      },
    );
    this.http.assertOk(res);
  }

  async removePushSubscription(
    identity: EventIdentity,
    payload: PushSubscriptionRemovePayload,
  ): Promise<void> {
    const pathId = identity.userId ?? identity.anonymousId;
    const res = await this.http.request<unknown>(
      'DELETE',
      `/users/${encodeURIComponent(pathId!)}/push-subscriptions`,
      {
        ...payload,
        ...this.anonymousBodyFields(identity),
      },
    );
    this.http.assertOk(res);
  }

  async trackClick(payload: {
    messageId: string;
    userId?: string;
    anonymousId?: string;
    anonymousIdProvider?: 'fingerprintjs' | 'internal';
    isForeground?: boolean;
  }): Promise<void> {
    const res = await this.http.request<unknown>('POST', '/track/click', payload);
    this.http.assertOk(res);
  }

  async trackOpen(payload: { messageId: string; isForeground?: boolean }): Promise<void> {
    const res = await this.http.request<unknown>('POST', '/track/open', payload);
    this.http.assertOk(res);
  }
}

export function createMotiSigApi(
  options: ConstructorParameters<typeof MotiSigHttpClient>[0]
): MotiSigApi {
  return new MotiSigApi(new MotiSigHttpClient(options));
}
